/**
 * 西国（九州・四国）の国データ
 *
 * 担当: 筑前(34)・肥前(35)・薩摩(36)・豊後(37)・伊予(38)
 *
 * 執筆基準は provinces/_reference.ts（尾張・甲斐）に揃える。
 * 石高ランクと防御値は docs/01-ゲームデザイン.md §2.2 の盤面表に従うバランス値であり、
 * historicalKokudaka（慶長3年＝1598年の国別石高／万石）とは別物である。
 * 両者の差は必ず gameNote で断ること（docs/05-学習設計.md §4-1）。
 */

import type { Province } from '../types';

/**
 * 筑前 — 博多を擁する大陸との窓口。地の利は鉄砲（第I章では足軽）。
 */
export const CHIKUZEN: Province = {
  id: 34,
  kind: 'province',
  name: '筑前',
  reading: 'ちくぜん',
  region: 'kyushu',
  kokudaka: 3,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 33.6,
  note: {
    summary:
      '玄界灘に面し、博多の湊を通じて大陸と結ばれた国。交易の利をめぐって大内・大友・毛利・島津が争った。',
    detail: {
      1: 'いまの福岡県の西がわにあった国です。博多という大きな港があり、中国や朝鮮とのあいだを船がゆきかいました。港の富をめぐって、大内・大友・毛利・島津といった大名が何度も争いました。商人の力が強い国でした。',
      2: '現在の福岡県西部にあたる国。玄界灘に面し、博多の湊が大陸との交易の窓口だった。守護は少弐氏だったが、博多の利益を求める周防の大内氏が進出して少弐氏を圧迫し、大内氏が滅びたあとは豊後の大友氏と安芸の毛利氏が立花山城をめぐって争った。1580年代には島津氏が北上して博多の町は焼けたが、1587年に豊臣秀吉が九州を平定したのち、神屋宗湛や島井宗室といった博多商人の協力で町が建てなおされた（太閤町割）。太閤検地の石高は約33.6万石。',
      3: '現在の福岡県西部。玄界灘に面し、古代には大宰府が置かれ、中世を通じて博多が対外交易の窓口であり続けた。日宋・日元貿易から日明貿易（勘合貿易）まで、大陸との人と物の出入りはここを通っている。室町期の守護は少弐氏だが、博多の利権を求めた周防の大内氏が進出して少弐氏を圧迫し、1551年に大内義隆が家臣の陶晴賢に討たれると、豊後の大友氏と安芸の毛利氏が博多と立花山城をめぐって争った。1580年代には島津氏が北上し、この間に博多の町は焼亡している。1587年の豊臣秀吉による九州平定ののち、神屋宗湛・島井宗室ら博多商人の協力を得て町の再建（太閤町割）が行われ、町は年行司を置く自治的な運営を保った。慶長3年（1598年）の記録では約33.6万石。ただし筑前の実力は石高よりも、博多の町衆が握った交易と金融にあったと見るのが一般的である。',
    },
    year: [1467, 1600],
    keywords: ['筑前', '博多', '少弐氏', '大内氏', '大友氏', '太閤町割'],
    caveat:
      '博多商人・神屋宗湛の曽祖父にあたる神屋寿禎が1533年に石見銀山へ灰吹法を伝えた、という話は後世の記録によるもので、同時代史料での裏づけは十分ではありません。天正年間に博多の町が焼けた経緯についても、誰の軍によるものかなど史料によって記述が異なります。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値です。実際の筑前は慶長3年（1598年）の記録で約33.6万石でした。また盤上では博多の交易を隣の33番「博多湊」マスで表しているため、筑前の国そのものには産出（yields）を置いていません。',
  },
};

/**
 * 肥前 — 南蛮船の寄港地が並んだ国。地の利は鉄砲（第I章では足軽）。
 */
export const HIZEN: Province = {
  id: 35,
  kind: 'province',
  name: '肥前',
  reading: 'ひぜん',
  region: 'kyushu',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 31.0,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '入り組んだ海岸と島々からなる国。平戸・長崎にポルトガル船が寄港し、南蛮貿易と布教の窓口となった。',
    detail: {
      1: 'いまの佐賀県と長崎県にあたる国です。海に面していて、島や入り江がたくさんあります。平戸や長崎にポルトガルの船が来て、めずらしい品物がもたらされました。キリスト教を信じる大名もあらわれました。',
      2: '現在の佐賀県と長崎県（壱岐・対馬を除く）にあたる国。海岸線が複雑で、松浦党と呼ばれる海の武士団が早くから活動した。1550年にフランシスコ・ザビエルが平戸へ来て以降、平戸・横瀬浦・長崎にポルトガル船が来航し、南蛮貿易と布教が同時に進んだ。1563年に大村純忠が洗礼を受け、1580年には長崎をイエズス会に寄進している。国内では龍造寺隆信が統一を進めたが、1584年の沖田畷の戦いで島津・有馬方に敗れて戦死し、以後は鍋島直茂が実権を握った。太閤検地の石高は約31万石。',
      3: '現在の佐賀県と長崎県（壱岐・対馬を除く）。リアス海岸と多数の島嶼からなり、松浦党と総称される在地の海上武士団が早くから活動した。1550年にフランシスコ・ザビエルが平戸へ来航して以降、平戸・横瀬浦・口之津・長崎が相次いでポルトガル船の寄港地となり、南蛮貿易と布教が並行して進んだ。大村純忠は1563年に洗礼を受け、1570年の長崎開港を経て1580年に長崎とその周辺をイエズス会へ寄進している（1587年の豊臣秀吉のバテレン追放令で公収）。1582年には大村・有馬・大友の名で天正遣欧使節がローマへ送られ、1590年に帰国した。国内政治では龍造寺隆信が肥前をほぼ統一したが、1584年の沖田畷の戦いで島津・有馬方に敗れて戦死し、実権は家臣の鍋島直茂へ移る。1592年からの朝鮮出兵では名護屋城が全国の大名を集める前線基地となり、この戦役で連れて来られた朝鮮人陶工の技術が唐津焼や、のちの有田の磁器生産の基礎となった。慶長3年（1598年）の記録では約31万石。',
    },
    year: [1467, 1600],
    keywords: ['肥前', '松浦党', '南蛮貿易', '大村純忠', '龍造寺氏', '名護屋城'],
    caveat:
      '長崎のイエズス会への寄進を「領土の割譲」と見るか、周辺勢力からの防衛を委ねた措置と見るかは、研究者によって評価が分かれます。有田の磁器生産の開始を李参平ひとりの功績とする通説も、近年の発掘調査によって見直しが進んでいます。なお1543年の鉄砲伝来の地は肥前ではなく、大隅国に属する種子島です。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値です。実際の肥前は慶長3年（1598年）の記録で約31万石あり、盤上でランク3としている薩摩（約28.3万石）よりも多い数字でした。また盤上では南蛮交易による銭の産出を常時のものとして扱っていますが、史実の南蛮船の来航は1550年以降に段階的に始まったものです。ゲーム上の解禁は第III章の南蛮交易マス化で表現します。',
  },
};

/**
 * 薩摩 — シラス台地の広がる南九州の国。地の利は弓。島津氏の本拠。
 */
export const SATSUMA: Province = {
  id: 36,
  kind: 'province',
  name: '薩摩',
  reading: 'さつま',
  region: 'kyushu',
  kokudaka: 3,
  defense: 4,
  terrain: 'mountain',
  historicalKokudaka: 28.3,
  note: {
    summary:
      '火山灰のシラス台地が広がり、水田に向かない国。鎌倉以来の島津氏が領し、戦国末には九州の広い範囲に及んだ。',
    detail: {
      1: 'いまの鹿児島県の西がわにあった国です。火山の灰がつもった土地が多く、水もちが悪いので米はあまりとれませんでした。島津氏が鎌倉時代からずっとこの地をおさめ、戦国時代には九州の広い範囲に力をのばしました。',
      2: '現在の鹿児島県西部にあたる国。シラスと呼ばれる火山灰の台地が広がり、水もちが悪いため水田に向かず、麦や粟などの畑作と海の生業が中心だった。島津氏は鎌倉時代の守護以来この地を領し、戦国大名としては例外的に古い家系である。一族の内紛を島津貴久がおさめ、その子の義久・義弘らの代に九州のほぼ全域へ勢力を広げた。1549年にはフランシスコ・ザビエルが鹿児島に上陸している。1587年に豊臣秀吉の軍が九州へ入ると義久は降伏し、薩摩・大隅などを安堵された。太閤検地の石高は約28.3万石。',
      3: '現在の鹿児島県西部。国土の多くをシラス台地が占め、火山灰質で保水性に乏しいため水田の適地が少なく、麦・粟・稗などの畑作と、海に依存する生業の比重が高かった。島津氏は鎌倉期の守護職以来この地を領し、戦国大名としては珍しく中世以来の家格をそのまま保った家である。15世紀から16世紀前半は薩州家・伊作家など一族間の抗争が続いたが、伊作家出身の島津貴久が国内を統一し、その子の義久・義弘・歳久・家久の代に大隅・日向を併せ、1578年の耳川、1584年の沖田畷などを経て九州のほぼ全域に影響を及ぼした。1549年にフランシスコ・ザビエルが鹿児島へ上陸し、日本での布教が始まった地でもある。1543年に鉄砲が伝わった種子島は大隅国に属するが島津氏の勢力下にあり、島津方が早い時期から鉄砲を用いたことが知られる。1587年に豊臣秀吉の軍が九州へ入ると義久は降伏して薩摩・大隅と日向の一部を安堵され、家臣を各地に分けて配置する仕組みは近世の外城制へ受け継がれた。慶長3年（1598年）の記録では約28.3万石。',
    },
    year: [1467, 1600],
    keywords: ['薩摩', '島津氏', 'シラス台地', '鹿児島', '九州平定'],
    caveat:
      '島津氏の戦法として知られる「釣り野伏せ」は、中央がわざと退いて敵を誘い込み、伏せておいた両翼で包囲するものと説明されますが、この呼称と体系だった説明は主に後世の軍記に由来し、同時代史料での裏づけは限られています。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値です。実際の薩摩は慶長3年（1598年）の記録で約28.3万石で、同じ九州の豊後（約41.8万石）や肥前（約31万石）より少ない数字でした。盤上でランクを高めにしているのは、島津氏の本拠として盤上の西端を支える役割を持たせたためです。また地形を山と置いて防御を高く設定していますが、実際の薩摩が攻め落とせない国だったわけではなく、1587年に豊臣軍の侵攻を受けて島津義久は降伏しています。',
  },
};

/**
 * 豊後 — 九州山地に抱かれた大友氏の本国。地の利は弓。
 */
export const BUNGO: Province = {
  id: 37,
  kind: 'province',
  name: '豊後',
  reading: 'ぶんご',
  region: 'kyushu',
  kokudaka: 2,
  defense: 3,
  terrain: 'mountain',
  historicalKokudaka: 41.8,
  note: {
    summary:
      '九州山地と火山群が国土の大半を占める国。府内を本拠とした大友氏のもとで、南蛮船の来航と西洋医学の伝来があった。',
    detail: {
      1: 'いまの大分県のほとんどにあたる国です。山が多く、温泉がわきます。大友氏がおさめ、府内（いまの大分市）にはポルトガルの船が来て、西洋のやり方で人を治す病院もつくられました。のちに島津氏との戦いにやぶれ、力をおとしました。',
      2: '現在の大分県の大部分にあたる国。九州山地やくじゅう連山が広がる山がちな国で、大野川・大分川の流域に平地が開ける。鎌倉時代以来の守護大友氏が府内（現在の大分市）を本拠とし、大友義鎮（宗麟）の代に北九州へ勢力を広げた。府内にはポルトガル船が来航し、1557年には宣教師アルメイダが西洋医学による病院を開いている。1578年の耳川の戦いで島津氏に大敗して以降は勢力が後退し、1593年に大友義統が改易されて大友氏の支配は終わった。太閤検地の石高は約41.8万石で、九州では最大だった。',
      3: '現在の大分県の大部分（北部の中津周辺は豊前）。九州山地とくじゅう連山、由布岳などの火山群が国土の大半を占め、大野川・大分川の下流に平野が開ける。別府や由布院の湯は中世からすでに知られていた。守護大友氏は鎌倉期以来の家柄で、府内（現在の大分市）を本拠とする。大友義鎮（宗麟）は1550年のいわゆる二階崩れの変で家督を継ぎ、1559年に室町幕府から九州探題に補任されて北九州へ勢力を広げた。府内にはポルトガル船が来航し、1557年には宣教師ルイス・デ・アルメイダが外科と内科を備えた施設と、捨て子を保護する育児院を開いている。義鎮は1578年に洗礼を受けたが、同年の耳川の戦いで島津氏に大敗し、以後は家中の離反が続いた。1586年からの島津氏の侵攻を受けて豊臣秀吉に救援を求め、九州平定の結果として子の義統が豊後を安堵されたものの、1593年に朝鮮出兵中の失態を理由に改易された。慶長3年（1598年）の記録では約41.8万石で、九州の国のなかでは最大である。',
    },
    year: [1467, 1600],
    keywords: ['豊後', '大友氏', '府内', '九州探題', '耳川の戦い', 'アルメイダ'],
    caveat:
      '大友義鎮（宗麟）の受洗の動機や、キリスト教にもとづく理想の国を築こうとしたという話は、宣教師側の記録に多くを負っており、研究者の評価が分かれています。耳川の戦いの参加兵力についても、史料によって数字が大きく異なります。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値です。実際の豊後は慶長3年（1598年）の記録で約41.8万石あり、九州では最大でした。盤上で低めに置いているのは、九州に強い国を並べすぎないための調整です。また地形は国土の大半を占める山地に合わせて「山」としています。府内の湊と南蛮貿易は、第II章のイベント「キリスト教伝来」による南蛮交易マス化で表現します。',
  },
};

/**
 * 伊予 — 四国山地と瀬戸内海にはさまれた国。地の利は鉄砲（第I章では足軽）。
 */
export const IYO: Province = {
  id: 38,
  kind: 'province',
  name: '伊予',
  reading: 'いよ',
  region: 'shikoku',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 36.6,
  note: {
    summary:
      '南に四国山地、北に瀬戸内海を抱く国。芸予諸島の村上氏が海上交通を差配し、河野氏が長く守護をつとめた。',
    detail: {
      1: 'いまの愛媛県にあたる国です。南には高い山があり、北には島がたくさんうかぶ瀬戸内海が広がっています。海の道を行き来する船から通行料をとり、船を守るしごとをした水軍がいました。河野氏という一族が長くこの国をおさめました。',
      2: '現在の愛媛県にあたる国。南に石鎚山を頂く四国山地、北に瀬戸内海が広がる。守護は河野氏で、道後の湯築城を本拠とした。芸予諸島では能島・来島・因島の村上氏が海上交通の要衝を押さえ、通行料を取って船の警固を請け負った。伊予の来島村上氏は1555年の厳島の戦いで毛利方に加わったと伝えられる。河野氏は大内氏や毛利氏と結んで土佐や豊後の勢力に対抗したが、1585年の豊臣秀吉による四国攻めで降伏し、大名家としての河野氏は終わった。太閤検地の石高は約36.6万石。',
      3: '現在の愛媛県。西日本最高峰の石鎚山を含む四国山地が南半を占め、北は瀬戸内海に面して芸予諸島が連なる。可耕地は道後平野・今治平野や宇和の谷筋に限られるが、瀬戸内の海運と製塩が国の富を支えた。守護は河野氏で、道後温泉のそばに築いた湯築城を本拠とする。芸予諸島を根拠とした能島・来島・因島の村上氏は、海上交通の結節点を押さえて帆別銭などの通行料を取り、船の警固を請け負う勢力として高い自立性を保った。伊予の来島村上氏は1555年の厳島の戦いで毛利方に加わったと伝えられる。河野氏は周防の大内氏、のちに安芸の毛利氏と結び、土佐の一条氏・長宗我部氏や豊後の大友氏の圧力に対抗したが、1585年の豊臣秀吉による四国攻めで小早川隆景の軍に降伏し、河野通直の死をもって大名家としては断絶した。慶長3年（1598年）の記録では約36.6万石。',
    },
    year: [1467, 1600],
    keywords: ['伊予', '河野氏', '村上氏', '湯築城', '瀬戸内海', '四国攻め'],
    caveat:
      '村上氏を「海賊」と記す史料はありますが、近年の研究では、無差別に略奪する集団というより、航路の安全を保証して対価を得る海上勢力としての性格が重視されています。厳島の戦いにおける来島村上氏の参戦の経緯についても、記録によって内容が異なります。',
    gameNote:
      '盤上の石高ランクは遊びやすさのための相対値です。実際の伊予は慶長3年（1598年）の記録で約36.6万石でした。またこのゲームでは長宗我部元親の開始国を伊予に置いていますが、史実の長宗我部氏の本拠は土佐であり、能力名に用いた「一領具足」も土佐の仕組みです。四国のマスを1つに絞った盤面の都合による配置です。',
  },
};
